// The places on the world view: a dot and a name per city, drawn in the
// map's own frame so that:
//
// - each city is drawn once, on the copy of the world nearest the middle of
//   the map beside the panel (a layer is drawn on every copy, and at world
//   zoom a wide window shows Sydney at both ends);
// - cities too close to tell apart at this zoom (Seattle and Portland) are
//   one dot, which zooms to them all;
// - a dot of up to MAX_NAMED cities is named: each name takes the first
//   side of its dot (below, above, right, left) that doesn't cover another
//   name or dot, or reach under the panel;
// - a dot of more is a bubble saying how many (Europe's 48 at world zoom),
//   not a column of names, and a tap zooms to them all.
//
// layoutMarkers is the pure part (test/markers.test.ts); createMarkers puts
// it on the page.

import type { GeoJSONSource, Map as MapLibreMap } from 'maplibre-gl';
import type { FeatureCollection, Point } from 'geojson';
import { installMapCanvasLayer, mapCanvasLayer } from '@tol/ui/map-canvas-layer';

export interface MarkerPoint {
  readonly id: string;
  readonly name: string;
  // Screen position (px).
  readonly x: number;
  readonly y: number;
}

export type Side = 'below' | 'above' | 'right' | 'left';

// [left, top, right, bottom], px.
export type Rect = readonly [number, number, number, number];

export interface PlacedMarker {
  // North to south.
  readonly ids: readonly string[];
  readonly names: readonly string[];
  // The label's lines: the names, one a line; none for a count.
  readonly lines: readonly string[];
  // How many cities a bubble stands for; null for a named dot.
  readonly count: number | null;
  readonly x: number;
  readonly y: number;
  readonly side: Side;
  readonly label: Rect;
}

// Dots nearer than this (px) are one.
export const MERGE_PX = 16;
export const DOT_R = 7;
// A dot of more cities than this is a count.
export const MAX_NAMED = 3;
// Clusters grow (MERGE_PX, then half as far again) until the names fit,
// up to this: past it the gaps are oceans, and a dot there stands for
// nowhere (North America and Europe were one dot mid-Atlantic on a phone).
const MAX_MERGE_PX = MERGE_PX * 4;
const GAP = 4;
const SIDES: readonly Side[] = ['below', 'above', 'right', 'left'];

// A count bubble's radius: room for its digits.
export const bubbleR = (count: number): number => Math.max(11, 4 + 4 * String(count).length);

const overlaps = (a: Rect, b: Rect): boolean => a[0] < b[2] && b[0] < a[2] && a[1] < b[3] && b[1] < a[3];
const inside = (a: Rect, b: Rect): boolean => a[0] >= b[0] && a[1] >= b[1] && a[2] <= b[2] && a[3] <= b[3];

function labelRect(x: number, y: number, side: Side, w: number, h: number): Rect {
  if (side === 'below') return [x - w / 2, y + DOT_R + GAP, x + w / 2, y + DOT_R + GAP + h];
  if (side === 'above') return [x - w / 2, y - DOT_R - GAP - h, x + w / 2, y - DOT_R - GAP];
  if (side === 'right') return [x + DOT_R + GAP, y - h / 2, x + DOT_R + GAP + w, y + h / 2];
  return [x - DOT_R - GAP - w, y - h / 2, x - DOT_R - GAP, y + h / 2];
}

// Clusters (single linkage within MERGE_PX) and a side for each label
// (searchSides).
// `measure` is a label's size for its lines, one name or two each. Points keep their
// order of priority: an earlier one's label is placed first. `clear`: the
// part of the map the panel doesn't cover, where labels should stay.
export function layoutMarkers(
  points: readonly MarkerPoint[],
  measure: (names: readonly string[]) => { readonly w: number; readonly h: number },
  clear: Rect,
  mergePx = MERGE_PX,
): PlacedMarker[] {
  // Union-find over pairs closer than mergePx.
  const parent = points.map((_, i) => i);
  const find = (i: number): number => {
    let r = i;
    while (parent[r] !== r) r = parent[r] ?? r;
    return r;
  };
  for (let i = 0; i < points.length; i++) {
    for (let j = i + 1; j < points.length; j++) {
      const a = points[i];
      const b = points[j];
      if (a && b && Math.hypot(a.x - b.x, a.y - b.y) < mergePx) parent[find(j)] = find(i);
    }
  }
  const groups = new Map<number, readonly MarkerPoint[]>();
  for (const [i, p] of points.entries()) {
    const root = find(i);
    groups.set(root, [...(groups.get(root) ?? []), p]);
  }
  interface Cluster {
    readonly members: readonly MarkerPoint[];
    readonly x: number;
    readonly y: number;
  }
  const clusters: readonly Cluster[] = [...groups.values()].map((g): Cluster => {
    const members = g.toSorted((a, b) => a.y - b.y);
    return {
      members,
      x: members.reduce((s, p) => s + p.x, 0) / members.length,
      y: members.reduce((s, p) => s + p.y, 0) / members.length,
    };
  });
  const counted = (c: Cluster): boolean => c.members.length > MAX_NAMED;
  const dots: Rect[] = clusters.map((c) => {
    const r = counted(c) ? bubbleR(c.members.length) : DOT_R;
    return [c.x - r, c.y - r, c.x + r, c.y + r];
  });
  // Each label's sides, and what each covers besides other labels: dots,
  // and the panel. A bubble has one "side", itself, which names avoid.
  const options = clusters.map((c, i): LabelOption[] => {
    const own = dots[i] ?? [c.x, c.y, c.x, c.y];
    if (counted(c)) return [{ side: 'below', rect: own, hits: 0, lines: [] }];
    const names = c.members.map((p) => p.name);
    const sides = (lines: readonly string[]): LabelOption[] => {
      const { w, h } = measure(lines);
      return SIDES.map((side) => {
        const rect = labelRect(c.x, c.y, side, w, h);
        const hits = dots.filter((r) => overlaps(r, rect)).length + (inside(rect, clear) ? 0 : 1);
        return { side, rect, hits, lines };
      });
    };
    return sides(names);
  });
  // No clean layout: cities a little further apart share dots too.
  const found = searchSides(options);
  // A dot on the open map whose name has no clean side counts as no layout.
  const stuck = options.some(
    (o: readonly LabelOption[], i) => o.every((x: LabelOption) => x.hits > 0) && inside(dots[i] ?? clear, clear),
  );
  if ((!found || stuck) && mergePx * 1.5 <= MAX_MERGE_PX) {
    return layoutMarkers(points, measure, clear, mergePx * 1.5);
  }
  const sides = found ?? greedySides(options);
  return clusters.map((c, i) => {
    const o = options[i]?.[sides[i] ?? 0];
    const names = c.members.map((p) => p.name);
    return {
      ids: c.members.map((p) => p.id),
      names,
      lines: o?.lines ?? names,
      count: counted(c) ? c.members.length : null,
      x: c.x,
      y: c.y,
      side: o?.side ?? 'below',
      label: o?.rect ?? dots[i] ?? [c.x, c.y, c.x, c.y],
    };
  });
}

interface LabelOption {
  readonly side: Side;
  readonly rect: Rect;
  readonly lines: readonly string[];
  // Dots it covers, plus 1 if it isn't wholly in the clear area.
  readonly hits: number;
}

// A side for every label covering nothing, if there is one: each label in
// turn takes its first side clear of the dots, the panel and the labels
// before it, going back to try an earlier label's next side when a later
// one has none. A label with no side clear of dots and panel (a city under
// the panel) keeps its least bad one and the others avoid it. Ten places
// search in a handful of steps; the cap keeps a pathological view cheap.
function searchSides(options: readonly (readonly LabelOption[])[]): number[] | null {
  const fixed = options.map((opts) => (opts.some((o) => o.hits === 0) ? null : leastBad(opts)));
  const chosen: number[] = [];
  let steps = 0;
  const fits = (i: number, rect: Rect): boolean => {
    for (let j = 0; j < options.length; j++) {
      if (j === i) continue;
      const k = j < i ? chosen[j] : (fixed[j] ?? null);
      const other = k === null || k === undefined ? undefined : options[j]?.[k]?.rect;
      if (other && overlaps(other, rect)) return false;
    }
    return true;
  };
  const place = (i: number): boolean => {
    if (i === options.length) return true;
    if (++steps > 5000) return false;
    const opts = options[i] ?? [];
    const f = fixed[i];
    if (f !== null && f !== undefined) {
      chosen[i] = f;
      return place(i + 1);
    }
    for (const [k, o] of opts.entries()) {
      if (o.hits !== 0 || !fits(i, o.rect)) continue;
      chosen[i] = k;
      if (place(i + 1)) return true;
    }
    return false;
  };
  return place(0) ? chosen : null;
}

function leastBad(opts: readonly LabelOption[]): number {
  let best = 0;
  for (const [k, o] of opts.entries()) if (o.hits < (opts[best]?.hits ?? Infinity)) best = k;
  return best;
}

// Without a clean layout: each label, in turn, the side covering the least
// (dots, the panel, the labels before it).
function greedySides(options: readonly (readonly LabelOption[])[]): number[] {
  const placed: Rect[] = [];
  return options.map((opts) => {
    let best = 0;
    let bestHits = Infinity;
    for (const [k, o] of opts.entries()) {
      const hits = o.hits + placed.filter((r) => overlaps(r, o.rect)).length;
      if (hits < bestHits) {
        best = k;
        bestHits = hits;
      }
    }
    const rect = opts[best]?.rect;
    if (rect) placed.push(rect);
    return best;
  });
}

export interface MarkerRegion {
  readonly id: string;
  readonly name: string;
  readonly center: readonly [number, number];
}

// The marker under one map-container point. Both dots and names keep the
// existing six-pixel tap allowance while all pointer gestures reach the map.
export function markerAt(
  placed: readonly PlacedMarker[],
  x: number,
  y: number,
  slop = 6,
  labels = true,
): PlacedMarker | undefined {
  return placed.find((marker) => {
    const radius = marker.count === null ? DOT_R : bubbleR(marker.count);
    const dot: Rect = [marker.x - radius, marker.y - radius, marker.x + radius, marker.y + radius];
    return (
      (x >= dot[0] - slop && x <= dot[2] + slop && y >= dot[1] - slop && y <= dot[3] + slop) ||
      (labels &&
        x >= marker.label[0] - slop &&
        x <= marker.label[2] + slop &&
        y >= marker.label[1] - slop &&
        y <= marker.label[3] + slop)
    );
  });
}

// The dots and names over `map`, from zoom 0 to hideZoom. They are painted
// into MapLibre's WebGL frame so a gesture cannot move the basemap without
// moving its places. A click on one calls go with its cities.
export function createMarkers(
  map: MapLibreMap,
  regions: readonly MarkerRegion[],
  options: {
    readonly hideZoom: number;
    // The part of the map not under the panel, in map-container px.
    readonly clear: () => Rect;
    readonly go: (ids: readonly string[]) => void;
  },
): void {
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new TypeError('no marker canvas context');
  const sizes = new Map<string, { w: number; h: number }>();
  const lineHeight = 14 * 1.15;
  const measure = (names: readonly string[]): { w: number; h: number } => {
    const key = names.join('\n');
    let size = sizes.get(key);
    if (!size) {
      ctx.font = '600 14px "Hanken Grotesk", system-ui, sans-serif';
      size = {
        w: Math.max(...names.map((name) => ctx.measureText(name).width)),
        h: lineHeight * names.length,
      };
      sizes.set(key, size);
    }
    return size;
  };

  let placed: PlacedMarker[] = [];
  const labelLayer = 'world-place-labels';
  const hasGlyphs = map.getStyle().glyphs !== undefined;
  const layout = (): PlacedMarker[] => {
    const [l, t, r, b] = options.clear();
    const mid = { x: (l + r) / 2, y: (t + b) / 2 };
    const worldPx = 512 * 2 ** map.getZoom();
    return layoutMarkers(
      regions.map((region) => {
        const point = map.project([region.center[0], region.center[1]]);
        const x = point.x + worldPx * Math.round((mid.x - point.x) / worldPx);
        return { id: region.id, name: region.name, x, y: point.y };
      }),
      measure,
      [l, t, r, b],
    );
  };
  if (hasGlyphs) {
    map.addSource(labelLayer, { type: 'geojson', data: { type: 'FeatureCollection', features: [] } });
    map.addLayer({
      id: labelLayer,
      type: 'symbol',
      source: labelLayer,
      maxzoom: options.hideZoom,
      layout: {
        'text-field': ['get', 'name'],
        'text-font': ['Montserrat Medium', 'Open Sans Bold'],
        'text-size': 14,
        'text-variable-anchor': ['top', 'bottom', 'left', 'right'],
        'text-radial-offset': 0.9,
        'text-justify': 'auto',
        'text-padding': 12,
        'text-allow-overlap': false,
        'text-ignore-placement': false,
        'symbol-sort-key': ['get', 'rank'],
      },
      paint: { 'text-color': '#F2F4FA', 'text-halo-color': '#0A1230', 'text-halo-width': 2 },
    });
    const updateLabels = (): void => {
      const data: FeatureCollection<Point> = {
        type: 'FeatureCollection',
        features: layout()
          .filter((m) => m.count === null)
          .map((m) => {
            const point = map.unproject([m.x, m.y]);
            return {
              type: 'Feature',
              geometry: { type: 'Point', coordinates: [point.lng, point.lat] },
              properties: { name: m.lines.join('\n'), ids: m.ids.join(','), rank: m.ids.includes('ch') ? 1 : 0 },
            };
          }),
      };
      void map.getSource<GeoJSONSource>(labelLayer)?.setData(data);
    };
    map.on('moveend', updateLabels);
    map.on('resize', updateLabels);
    updateLabels();
  }
  let revision = 0;
  let lastState = '';
  const layer = mapCanvasLayer('world-markers', canvas, () => {
    const zoom = map.getZoom();
    if (zoom >= options.hideZoom) {
      placed = [];
      lastState = 'hidden';
      return 'skip';
    }
    const mapCanvas = map.getCanvas();
    if (canvas.width !== mapCanvas.width || canvas.height !== mapCanvas.height) {
      canvas.width = mapCanvas.width;
      canvas.height = mapCanvas.height;
      sizes.clear();
    }
    const ratio = map.getPixelRatio();
    const center = map.getCenter();
    const [l, t, r, b] = options.clear();
    const state = [
      canvas.width,
      canvas.height,
      ratio,
      center.lng,
      center.lat,
      zoom,
      map.getBearing(),
      map.getPitch(),
      l,
      t,
      r,
      b,
      revision,
    ].join('|');
    if (state === lastState) return 'reuse';
    lastState = state;
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.setTransform(ratio, 0, 0, ratio, 0, 0);

    placed = layout();
    ctx.globalAlpha = Math.min(1, options.hideZoom - zoom);
    for (const marker of placed) {
      const radius = marker.count === null ? DOT_R : bubbleR(marker.count);
      if (marker.count !== null) {
        ctx.beginPath();
        ctx.arc(marker.x, marker.y, radius + 3, 0, Math.PI * 2);
        ctx.fillStyle = 'rgba(245, 181, 17, 0.28)';
        ctx.fill();
      }
      ctx.beginPath();
      ctx.arc(marker.x, marker.y, radius, 0, Math.PI * 2);
      ctx.fillStyle = '#F5B511';
      ctx.fill();
      ctx.lineWidth = 2;
      ctx.strokeStyle = '#0A1230';
      ctx.stroke();
      if (marker.count !== null) {
        ctx.fillStyle = '#0A1230';
        ctx.font = '700 12px "Hanken Grotesk", system-ui, sans-serif';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(String(marker.count), marker.x, marker.y + 0.5);
        continue;
      }
      if (hasGlyphs) continue;
      ctx.font = '600 14px "Hanken Grotesk", system-ui, sans-serif';
      ctx.textAlign = marker.side === 'left' ? 'right' : marker.side === 'right' ? 'left' : 'center';
      ctx.textBaseline = 'top';
      ctx.lineWidth = 5;
      ctx.lineJoin = 'round';
      ctx.strokeStyle = '#0A1230';
      ctx.fillStyle = '#F2F4FA';
      const x = marker.side === 'left' ? marker.label[2] : marker.side === 'right' ? marker.label[0] : marker.x;
      for (const [i, name] of marker.lines.entries()) {
        const y = marker.label[1] + i * lineHeight;
        ctx.strokeText(name, x, y);
        ctx.fillText(name, x, y);
      }
    }
    ctx.globalAlpha = 1;
    return 'upload';
  });
  installMapCanvasLayer(map, layer);
  void document.fonts.ready.then(() => {
    sizes.clear();
    revision++;
    map.triggerRepaint();
    return null;
  });

  map.on('click', (event) => {
    const ids: unknown = hasGlyphs
      ? map.queryRenderedFeatures(event.point, { layers: [labelLayer] })[0]?.properties?.['ids']
      : undefined;
    if (typeof ids === 'string') {
      options.go(ids.split(','));
      return;
    }
    const marker = markerAt(placed, event.point.x, event.point.y, 6, !hasGlyphs);
    if (marker) options.go(marker.ids);
  });
  // The pointer over a marker; the shell sets the cursor for its trains.
  let over = false;
  map.on('mousemove', (event) => {
    const now =
      markerAt(placed, event.point.x, event.point.y, 6, !hasGlyphs) !== undefined ||
      (hasGlyphs && map.queryRenderedFeatures(event.point, { layers: [labelLayer] }).length > 0);
    if (now !== over) map.getCanvas().style.cursor = now ? 'pointer' : '';
    over = now;
  });
}
